import { GridCellProps } from '@progress/kendo-react-grid';
import { useCallback, useMemo } from 'react';
import useConfirm from 'src/hooks/useConfirm';
import usePeriodView from 'src/hooks/usePeriodView';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import { informationIcon } from 'src/utils/icons';
import GridCellBase, { useGridCellDisplayValue } from '../GridCellBase';

const GlCodeApprovalCell = (props: GridCellProps) => {
  const field = props?.field || '';
  const value = props?.dataItem?.[field];
  const dataItem = props?.dataItem;
  const { displayValue } = useGridCellDisplayValue(props);
  const {
    setOpenUsersAssigned,
    clients,
    setAssignedUsers,
    hasManageGLCodeAttributesPermission,
    isEditMode,
    isPeriodTableDirty,
    isCheckbookEditable,
  } = useViewCheckbook();
  const {
    glCodeAtrPanelOpen,

    handleGLCodeAttributes,
  } = usePeriodView();
  const { confirm } = useConfirm();

  const { isActive } = props.dataItem;

  const settingAssignedUsers = useCallback(
    (e: any) => {
      e.stopPropagation();
      setOpenUsersAssigned(true);

      const clientIds =
        field === 'invoiceApproval'
          ? dataItem?.invoiceGlApproval?.clientIds || []
          : field === 'poApproval'
          ? dataItem?.poGlApproval?.clientIds || []
          : [];

      const users = clientIds.map((att: number) => {
        const user = clients.find((client) => client.clientId === att);
        return user
          ? {
              ...user,
              glCode: dataItem?.code,
              description: dataItem?.description,
              status: field === 'invoiceApproval' ? `Invoice - ${value}` : `PO - ${value}`,
            }
          : null;
      });
      const assignedUsersDetails = {
        title:
          value.toString().trim().toLowerCase() === 'required'
            ? 'Always Required'
            : 'Always Exempt',
        users,
        glCodeDescription: `${dataItem?.code} - ${dataItem?.description}`,
      };
      setAssignedUsers(assignedUsersDetails);
    },
    [
      clients,
      dataItem?.code,
      dataItem?.description,
      dataItem?.invoiceGlApproval?.clientIds,
      dataItem?.poGlApproval?.clientIds,
      field,
      setAssignedUsers,
      setOpenUsersAssigned,
      value,
    ]
  );

  const handleManageGLCodeAttribute = useCallback(
    (e: any) => {
      e.stopPropagation();
      handleGLCodeAttributes(dataItem);
      glCodeAtrPanelOpen();
    },
    [dataItem, glCodeAtrPanelOpen, handleGLCodeAttributes]
  );

  const handleManageGLCodeAttributeClick = useCallback(
    (e: any) => {
      if (isPeriodTableDirty) {
        confirm({
          title: 'Unsaved Changes',
          content:
            'You have unsaved changes. Please go back (cancel) to first save them. Otherwise, you can proceed and your changes will be lost.',
          cancelButtonText: 'Cancel',
          confirmButtonText: 'Discard and Proceed',
          width: '725px',
        }).then((isAccepted) => {
          if (isAccepted) {
            handleManageGLCodeAttribute(e);
          }
        });
      } else {
        handleManageGLCodeAttribute(e);
      }
    },
    [isPeriodTableDirty, confirm, handleManageGLCodeAttribute]
  );

  const isEditable = useMemo(
    () => isEditMode && hasManageGLCodeAttributesPermission && isCheckbookEditable,
    [hasManageGLCodeAttributesPermission, isCheckbookEditable, isEditMode]
  );

  const isApprovalOff = displayValue === 'OFF';

  const button = (
    <div className="flex justify-center items-center cursor-pointer">
      <button
        onClick={handleManageGLCodeAttributeClick}
        className={`mr-0 ml-0 text-link cursor-pointer border-b border-b-blue-500 ${
          isApprovalOff ? '' : 'w-[53px]'
        }`}
      >
        {displayValue}
      </button>
      {value && !isApprovalOff && (
        <button onClick={settingAssignedUsers} className="mr-0 ml-1 p-0">
          {informationIcon}
        </button>
      )}
    </div>
  );
  const text = (
    <div className="flex justify-center items-center">
      <span className="mr-0 ml-0 w-[53px]">{displayValue}</span>
      {value && !isApprovalOff && (
        <button onClick={settingAssignedUsers} className="mr-0 ml-1 p-0">
          {informationIcon}
        </button>
      )}
    </div>
  );

  return (
    <GridCellBase {...props} title={value} rowSelectionEnabled={false}>
      {isEditable && isActive && !dataItem?.warnings?.isActive.length ? button : text}
    </GridCellBase>
  );
};

export default GlCodeApprovalCell;
